import React, { useRef } from "react";
import {
	FONT_COLOR,
	FONT_SIZE,
	SquareImgTile,
	Text,
	genClassName,
} from "@disco/disco_core";

import "./lead-gen-product.css";
import { PRODUCT_CLICK_TARGET } from "../../event_conf";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import productEventPropertyMapper from "../../utils/productEventPropertyMapper";
import getImgUrl from "../../utils/getImgUrl";
import useCachedViewTrack, {
	TRACKED_TARGET,
} from "../../hooks/useCachedViewTrack";
import { SNOWPLOW_EVENT } from "../../utils/snowplowEventMigrator";
import useElementInView from "../../hooks/useElementInView";
import useImpression from "../../hooks/useImpression";
import { generateProductUid } from "../WidgetCard/ProductCard";
import useExperience from "../../hooks/useExperience";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";

import linkProvider from "../../utils/linkProvider";
import useLeadGenV0Config, {
	LEAD_GEN_FEATURE,
} from "../../hooks/useLeadGenV0Config";
const parsePrice = (price) => {
	return isNaN(price) ? price : Number(price).toFixed(2);
};

const LeadGenProduct = ({
	brand,
	brandIdx,
	idx,
	product,
	showPrices = false,
	isParentInView = true,
}) => {
	const productContainerRef = useRef(null);
	const MAX_PRODUCT_IMG_SIZE = 250;

	const { isNurtureV1 } = useExperience();

	const { getFeatureConfig } = useLeadGenV0Config();

	const { SHOULD_REDIRECT_ON_CLICK: shouldRedirectOnClick } =
		getFeatureConfig({ feature: LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW });

	const canRedirectOnClick = isNurtureV1 || shouldRedirectOnClick;

	const track = useTrack();

	const { id: productId, discounted_price_str: productPrice } = product || {};

	const defaultEventProps = {
		...productEventPropertyMapper({
			brand,
			brandIdx,
			idx,
			product,
		}),
		initiatingTarget: PRODUCT_CLICK_TARGET.INLINE_PRODUCT_TILE,
	};

	const { isPartiallyInView } = useElementInView({
		elementRef: productContainerRef,
	});

	useCachedViewTrack({
		id: `${productId}_nurture`,
		key: TRACKED_TARGET.PRODUCT,
		event: SNOWPLOW_EVENT.WIDGET_PRODUCT_DISPLAY,
		properties: defaultEventProps,
		canTrack: isParentInView && isPartiallyInView,
	});

	const productUid = `${generateProductUid(product)}_nurture`;
	useImpression({
		id: productUid,
		cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_PRODUCT,
		event: SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_PRODUCT_DISPLAY,
		properties: defaultEventProps,
		target: productContainerRef.current,
	});

	const productRedirectUrl = getParsedRedirectUrl({
		brand,
		redirectUrl: product?.masked_url,
	});

	const handleProductClick = () => {
		track(eventTypes.CLICK, {
			...defaultEventProps,
			isInternalClick: !canRedirectOnClick,
		});
	};

	const isNonZeroPrice = parsePrice(productPrice) > 0;

	return (
		<SquareImgTile
			className={genClassName({
				base: "lead-gen-product",
			})}
			rounded
			bordered
			shadow={!isNurtureV1}
			src={getImgUrl(product, MAX_PRODUCT_IMG_SIZE, MAX_PRODUCT_IMG_SIZE)}
			key={productId}
			width={"100%"}
			onClick={handleProductClick}
			ref={productContainerRef}
			data-intersectionuid={productUid}
			{...linkProvider({
				href: productRedirectUrl,
				disabled: !canRedirectOnClick,
			})}
		>
			{showPrices && isNonZeroPrice && (
				<Text
					size={FONT_SIZE.BODY}
					color={FONT_COLOR.WHITE}
					className="lead-gen-product-price"
					vPadding={"mc"}
					hPadding={"mc"}
				>
					${parsePrice(productPrice)}
				</Text>
			)}
		</SquareImgTile>
	);
};
export default LeadGenProduct;
